import { captureException, getRavenClient } from 'utils/SentryHelpers'
import { isIosCheck, isMobileCheck } from 'utils/UserAgentHelper'
import { setAccessToken } from '@driftt/web-auth'
import { APP_URL, LOGIN_APP_URL, AUTH_CLIENT_ID } from 'constants/ResourcesConstants'
import { fetchTokenForDriftt } from 'app/api/TokenAPI'
import { authenticateWithCode, redirectToLoginPage, checkForGlobalSession } from '@driftt/web-auth'
import { omit } from 'ramda'
import ACCESS from 'utils/Access';

const CONVERSATIONS_URL_REGEX = /inboxes\/\d+\/conversations\/(\d+)/

/**
 * config used for allowed path for each gate
 * @type {{premium: RegExp, chat: RegExp}}
 */
const ALLOWED_PATHS = {
  premium_drift: /./,
  chat: /^\/(inboxes|settings(?!\/notifications-beta($|\/))|dashboard|live|conversation-stats|setup|users|customize(?!\/audience($|\/)))/,
  has_11_2016_targeting: /^\/customize\/audience/,
  has_messaging_for_all: /^\/(inboxes|settings)($|\/)/,
}

function isWhiteGloveRoute(pathname) {
  return pathname && pathname.includes('white-glove')
}

async function getLogInRedirect(app) {
  try {
    await app.stores.UserStore.getOrFetchCurrentUser().toPromise()
  } catch(e) {
    console.warn(e)
    redirectToLoginPage(AUTH_CLIENT_ID)
  }
}

function conversationsDeepLinkRedirect(pathname) {
  const conversationId = CONVERSATIONS_URL_REGEX.exec(pathname)[1]
  window.location = `drift://conversation/${conversationId}`
}

const removeCodeFromQuery = ({ replace, pathname, query }) => {
  const newQuery = omit(['code'], query)
  replace({ pathname, query: newQuery })
}

const sanitizeTokenFromHistory = ({ replace, pathname, query }) => {
  const newQuery = _.cloneDeep(query)
  delete newQuery.token
  delete newQuery.driftt_token
  replace({ pathname, query: newQuery })
}

export function onLoggedInRouteChange(prevState, nextState, replace, callback) {
  const { query, pathname } = nextState.location

  if (query.token || query.driftt_token) {
    sanitizeTokenFromHistory({ replace, pathname, query })
  }

  if (query.code && !isWhiteGloveRoute(pathname)) {
    removeCodeFromQuery({ replace, pathname, query })
  }

  callback()
}

const CONSUMED_CODES = []

export async function onLoggedInRouteEnter(app, nextState, replace) {
  const { query, pathname } = nextState.location

  await checkForGlobalSession()

  let processingCredential = false

  try {
    if (query.code && !isWhiteGloveRoute(pathname)) {
      if(CONSUMED_CODES.includes(query.code)) {
        return
      }
      CONSUMED_CODES.push(query.code)
      processingCredential = true
      await authenticateWithCode({ authorizationCode: query.code, clientId: AUTH_CLIENT_ID })
    }

    /**
     * This is how we accept token on oauth redirect from start.driftt.com
     */
    if (query.token) {
      processingCredential = true
      setAccessToken({ token: query.token })
    }
    // try exchange this one time token for an access token
    if (query.driftt_token) {
      processingCredential = true
      const response = await fetchTokenForDriftt(query.driftt_token)
      const body = await response.json()
      if (body.access_token) {
        setAccessToken({ token: body.access_token })
        const currentUser = await app.stores.UserStore.getOrFetchCurrentUser().toPromise()
        await app.queries.UserQueries.updateUser({
          userId: currentUser.id,
          params: {
            status: 'ENABLED',
          },
        })
      } else {
        const raven = getRavenClient()
        raven && raven.captureMessage('Bad Invitation Token!', { level: 'warn' })
        console.warn(body)
      }
    }
    // Remove token or driftt_token from query param after processing
    if (query.code && !isWhiteGloveRoute(pathname)) {
      removeCodeFromQuery({ replace, pathname, query })
    }
    else if (query.token || query.driftt_token) {
      sanitizeTokenFromHistory({ replace, pathname, query })
    }
    else if (isMobileCheck() && pathname !== '/mobile-lander') {
      if (CONVERSATIONS_URL_REGEX.test(pathname)) {
        conversationsDeepLinkRedirect(pathname)
      } else {
        window.location = isIosCheck()
          ? `${LOGIN_APP_URL}/download-ios-app`
          : `${LOGIN_APP_URL}/download-android-app`
      }
    }
    else {
      const authRedirect = await getLogInRedirect(app)
      if (authRedirect) {
        // This is super special case. Super super. It is due to going outside the app
        // is not part of the router's concern
        if (Marty.isBrowser) {
          window.location.href = authRedirect
        } else {
          replace({
            state: {
              external: authRedirect,
            },
          })
        }
      }
    }
  } catch(e) {
    captureException(e)
    return processingCredential
  } finally {
    return processingCredential
  }
}

async function getOnboardingRedirectPath(app, nextState) {
  const { query } = nextState.location

  const [orgProperties, userProperties] = await Promise.all([
    app.stores.OrgPropertiesStore.fetchPropertiesForCurrentOrg().toPromise(),
    app.stores.UserPropertiesStore.fetchPropertiesForCurrentUser().toPromise(),
  ])

  const { postInstall } = orgProperties

  if (postInstall === 'slack') {
    return 'setup-slack'
  }
}

export async function onProtectedRouteEnter(app, nextState, replace, callback) {
  try {
    const { pathname } = nextState.location
    const redirectPath =
      (await getOnboardingRedirectPath(app, nextState)) ||
      getGateRedirect(await app.stores.GateStore.getOrFetchGates().toPromise(), pathname)
    // redirect != pathname to prevent infinite replacement loop. possible
    // react-router bug
    if (redirectPath && redirectPath.includes('letsgo')) {
      window.location = redirectPath
    }
    if (redirectPath && redirectPath != pathname) {
      replace({ pathname: redirectPath })
    }
  } catch(e) {
    captureException(e)
  } finally {
    callback()
  }
}

export function getGateRedirect(gates, pathname) {
  const isAllowed = _.some(
    gates,
    (value, gate) => !!value && ALLOWED_PATHS[gate] && ALLOWED_PATHS[gate].test(pathname)
  )
  if (!isAllowed) {
    return gates.chat || gates.premium_drift ? '/' : '/letsgo'
  }
}



// WEBPACK FOOTER //
// ./src/scripts/app/RouteConfig.js